import React, { useEffect, useState } from 'react';
import { formatMoney, formatBudget, pinTitle } from '../utils/dealExtras';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { getLocalUser } from '../supabase';
import { getPinHistory, reactivatePin } from '../api/atlast';
import { ItineraryLeg } from '../ItineraryLeg';
import { watchOutcome, OUTCOME_STYLE } from '../utils/fee';
import AppNav from '../AppNav';

// TripHistory — new page (Sep 5, per Ali's report). Started as a
// Dashboard-embedded collapsible section the same day, then genuinely
// revised to a real dedicated page once it became clear compact text
// rows couldn't show the one thing that actually mattered: the real
// last-found flight itinerary a historic pin had. Flight pins only,
// per Ali's explicit call — hotel pins are a dead end product-wide.
//
// Colors reworked (Sep 5, per Ali's report — real feedback on the
// first version, which went too far toward a custom sepia palette and
// ended up feeling like a different, unbranded product bolted onto
// FlyAtlast). Same theme tokens as the Dashboard now — same navy,
// same white cards, same fonts — with the "this is archived, not
// live" signal carried by treatment instead of a whole new palette:
// a muted divider-gray border instead of the bold 2px navy every
// active card gets, a small ARCHIVED tag, and no action buttons except
// the one real action that can still legitimately apply here
// (Reactivate, only where it's genuinely still possible).
function formatDate(iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return null;
  const d = iso.includes('T') ? new Date(iso) : new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', opts);
}

// Same color convention Dashboard/Purchase History already use for a
// resolved freeze — kept = navy (theme.structural), refunded = green
// (theme.success). Cancelled/anything else gets a neutral muted gray
// rather than inventing a third color with no established meaning.
//
// payment_status override added (Sep 16, per Ali's report — real bug,
// confirmed live end-to-end: approving an evidence submission only
// ever updates payment_status (to 'kept_then_refunded_by_evidence'),
// never the status column itself, since a "kept" resolution that got
// reversed by evidence afterward is a separate axis from the
// resolution outcome itself, not a different one — Dashboard.js and
// PaymentMethodScreen.js both already check this same field for
// exactly this reason. This was the one screen that only ever read
// status, so it kept showing "fee kept" forever even after a real,
// confirmed Stripe refund had already gone out.
function HistoryCard({ pin, onReactivated }) {
  const [expanded, setExpanded] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState(null);
  const navigate = useNavigate();

  const dateStr = pin.departure_date
    ? `${formatDate(pin.departure_date)}${pin.return_date ? ' – ' + formatDate(pin.return_date) : ''}`
    : (pin.travel_month || 'No date set');
  const endedStr = formatDate(pin.deactivated_at) || 'unknown';
  // Label from the real reason the pin ended (Oct 10, per Ali's report: Perth,
  // Oslo and others said "Lifetime limit reached" although they had not used
  // their checks, because lifetime_cap_reached is the general "permanently
  // retired" flag, also set when a Price Watch completes or a trip date passes).
  const todayIso = new Date().toISOString().slice(0, 10);
  let outcome = 'Stopped tracking';
  if (pin.had_price_freeze) outcome = 'Price Watch complete';
  else if ((pin.check_count || 0) >= 25) outcome = 'Check limit reached';
  else if (pin.lifetime_cap_reached && pin.departure_date && String(pin.departure_date).slice(0, 10) <= todayIso) outcome = 'Trip date passed';
  else if (pin.lifetime_cap_reached) outcome = 'Trip ended';
  const paxLabel = `${pin.adults || 1} adult${(pin.adults || 1) !== 1 ? 's' : ''}${pin.child_ages && pin.child_ages.length > 0 ? `, ${pin.child_ages.length} child${pin.child_ages.length !== 1 ? 'ren' : ''}` : ''}`;
  const hasItinerary = !!pin.best_value_flight;
  const lastFoundStr = formatDate(pin.last_checked || pin.deactivated_at);

  const handleReactivate = async (e) => {
    e.stopPropagation();
    setReactivating(true);
    setReactivateError(null);
    try {
      await reactivatePin(pin.id);
      onReactivated(pin.id);
    } catch (error) {
      setReactivateError(error?.response?.data?.detail || 'Could not reactivate this pin. Try again.');
      setReactivating(false);
    }
  };

  return (
    <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', marginBottom: '12px' }}>
      <div
        onClick={() => setExpanded(v => !v)}
        role="button"
        style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', cursor: 'pointer' }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '16px', fontWeight: 800, color: theme.structural }}>
              {pinTitle(pin) || `${pin.origin_code} → ${pin.destination_code}`}
            </span>
            <span style={{ fontFamily: theme.fontMono, fontSize: '10px', fontWeight: 800, color: '#4A5F78', background: '#EAF1F8', padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.08em' }}>
              ARCHIVED
            </span>
          </div>
          <div style={{ fontSize: '13px', color: theme.textMuted, marginTop: '5px' }}>
            {dateStr} · {paxLabel} · Budget {formatBudget(pin.budget)} {pin.currency || 'CAD'} · {pin.check_count || 0} checks
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {pin.can_reactivate && (
            <button
              onClick={handleReactivate}
              disabled={reactivating}
              style={{
                background: reactivating ? theme.divider : theme.action, color: '#1a1a1a', border: 'none',
                borderRadius: '999px', padding: '9px 18px', fontSize: '13px', fontWeight: 800, cursor: reactivating ? 'default' : 'pointer',
                fontFamily: theme.fontDisplay
              }}
            >
              {reactivating ? 'Reactivating...' : '↻ Reactivate'}
            </button>
          )}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12.5px', color: theme.textMuted, fontWeight: 700 }}>{outcome}</div>
            {endedStr !== 'unknown' && <div style={{ fontSize: '12px', color: theme.textMuted }}>Ended {endedStr}</div>}
          </div>
          <span style={{ fontSize: '12.5px', color: theme.structural, fontWeight: 800, whiteSpace: 'nowrap', border: '1.5px solid #C3CFDA', borderRadius: '999px', padding: '7px 14px' }}>
            {expanded ? 'Hide ▲' : (hasItinerary ? 'See flight ▼' : 'Details ▼')}
          </span>
        </div>
      </div>

      {reactivateError && (
        <p style={{ color: theme.danger, fontSize: '12.5px', margin: '0 20px 10px' }}>{reactivateError}</p>
      )}

      {/* Price-Freeze cross-link (Sep 5, per Ali's report) — light
          version: a link to Payment History, not a duplicate of its
          data. Same color convention Dashboard already uses for a
          resolved freeze (navy=kept, green=refunded) so this reads as
          the same signal, just relocated.

          Real numbers added (Sep 15, per Ali's report — real gap: the
          color already told a user kept vs. refunded, but the text was
          identical either way, forcing a click into Payment History
          just to see what the color implied). Falls back to the old
          generic line if freeze_frozen_price is missing for any reason
          (older data, or the lookup failing server-side) — same
          fail-open pattern already used elsewhere on this page. */}
      {pin.had_price_freeze && (
        <div
          onClick={(e) => { e.stopPropagation(); navigate('/payment-method'); }}
          role="button"
          style={{
            margin: '0 20px 16px', padding: '12px 16px', borderRadius: '12px', background: OUTCOME_STYLE[watchOutcome(pin.freeze_status, pin.freeze_payment_status, pin.freeze_fee_amount)].bg,
            color: OUTCOME_STYLE[watchOutcome(pin.freeze_status, pin.freeze_payment_status, pin.freeze_fee_amount)].fg, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap'
          }}
        >
          <span>
            
            {pin.freeze_frozen_price != null
              ? `Starting price ${formatMoney(pin.freeze_frozen_price)} ${pin.freeze_currency || pin.currency || 'CAD'} — ${
                  pin.freeze_payment_status === 'kept_then_refunded_by_evidence'
                    ? (pin.freeze_fee_amount != null ? `paid more — ${formatMoney(pin.freeze_fee_amount)} ${pin.freeze_currency || pin.currency || 'CAD'} refunded` : 'paid more — fee refunded')
                    : pin.freeze_status === 'refunded'
                      // Real accuracy fix alongside the rename (Sep 23):
                      // this branch fires whether the price ended ABOVE
                      // the upper line or BELOW the lower one — "price
                      // rose" was only ever half true. "You paid
                      // nothing" is correct either way and matches the
                      // wording already used everywhere else this
                      // outcome appears (Payment History, the result
                      // email, the Watch screen).
                      ? 'you paid nothing'
                      : pin.freeze_status === 'kept'
                        ? (pin.freeze_fee_amount == null ? 'fee charged' : (Number(pin.freeze_fee_amount) === 0 ? 'trial, nothing charged' : `${formatMoney(pin.freeze_fee_amount)} ${pin.freeze_currency || pin.currency || 'CAD'} fee charged`))
                        : 'see result in Payment History'
                }`
              : 'This trip had a Price Watch — see it in Payment History'}
          </span>
          <span style={{ fontSize: '12px', fontWeight: 700, background: OUTCOME_STYLE[watchOutcome(pin.freeze_status, pin.freeze_payment_status, pin.freeze_fee_amount)].btn, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>View →</span>
        </div>
      )}

      {expanded && (
        <div style={{ borderTop: '1px solid #DDE3E7', padding: '18px 20px' }}>
          {hasItinerary ? (
            <>
              <div style={{
                fontSize: '12px', color: theme.textMuted, fontStyle: 'italic', marginBottom: '12px',
                background: '#EAF1F8', padding: '9px 14px', borderRadius: '10px'
              }}>
                🔍 Last found on {lastFoundStr} — not live, the real price may have changed since.
              </div>
              <ItineraryLeg label="Outbound" flight={pin.best_value_flight} />
              {pin.best_value_flight_return && (
                <ItineraryLeg label="Return" flight={pin.best_value_flight_return} />
              )}
              {pin.last_price && (
                <div style={{ fontSize: '12px', color: theme.textPrimary, fontWeight: 700, marginTop: '8px' }}>
                  Last price found: {formatMoney(pin.last_price)} {pin.last_price_currency || pin.currency || 'CAD'}
                  {/* Budget parity (Sep 15, per Ali's report — full
                      parity with the collapsed header's own "X
                      under/over" figure, which sat right above this
                      exact number but wasn't restated down here). Only
                      compares when both figures share a currency — a
                      cross-currency compare here would need the same
                      conversion Dashboard already does elsewhere, not
                      worth pulling in for this one line. */}
                  {pin.budget && (!pin.last_price_currency || pin.last_price_currency === (pin.currency || 'CAD')) && (
                    <span style={{ fontWeight: 400, color: theme.textMuted }}>
                      {' '}({formatMoney(Math.abs(pin.budget - pin.last_price))} {pin.last_price <= pin.budget ? 'under' : 'over'} budget)
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <p style={{ fontSize: '12.5px', color: theme.textMuted, margin: 0 }}>
              No flight was ever found for this pin before it stopped tracking.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TripHistory() {
  const navigate = useNavigate();
  const [pins, setPins] = useState(null);
  const [error, setError] = useState(null);
  const [justReactivated, setJustReactivated] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await getLocalUser();
        if (!user) {
          navigate('/login');
          return;
        }
        const res = await getPinHistory(user.id);
        setPins(res.pins || []);
      } catch (e) {
        console.error('Error loading trip history:', e);
        setError('Could not load your trip history. Try again.');
      }
    })();
  }, [navigate]);

  // A reactivated pin is active again — it's no longer history, it
  // belongs back on the Dashboard. Removed from this list immediately
  // rather than left sitting here in a now-inaccurate state.
  const handleReactivated = (pinId) => {
    const reactivatedPin = pins.find(p => p.id === pinId);
    setPins(prev => prev.filter(p => p.id !== pinId));
    setJustReactivated(reactivatedPin?.destination || 'Trip');
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg }}>
      <AppNav current="history" />

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '28px 20px 60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          
          <h1 style={{ fontFamily: theme.fontDisplay, fontSize: '26px', fontWeight: 800, color: theme.structural, margin: 0 }}>
            Trip History
          </h1>
        </div>
        <p style={{ fontSize: '13.5px', color: theme.textMuted, marginTop: '4px', marginBottom: '18px' }}>
          Trips no longer active on your Dashboard — including the real last flight we found for each one. Nothing here is live or updating anymore.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>Past trips</span>
          <span style={{ flex: 1, height: '1px', background: '#C3CFDA' }} />
        </div>

        {justReactivated && (
          <div style={{ background: theme.action, color: '#1a1a1a', padding: '13px 18px', borderRadius: '12px', marginBottom: '16px', fontSize: '13px', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span>✓ {justReactivated} is tracking again.</span>
            <span onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Go to Dashboard →</span>
          </div>
        )}

        {error && <p style={{ color: theme.danger, fontSize: '13px' }}>{error}</p>}

        {!error && pins === null && (
          <p style={{ color: theme.textMuted, fontSize: '13px' }}>Loading your trip history...</p>
        )}

        {!error && pins !== null && pins.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: theme.textMuted }}>
            <p style={{ fontSize: '14px', marginBottom: '4px' }}>Nothing here yet.</p>
            <p style={{ fontSize: '12.5px' }}>Trips fall off your Dashboard 30 days after they stop tracking — that's when they'll show up here instead.</p>
          </div>
        )}

        {!error && pins !== null && pins.length > 0 && (
          <div>
            {pins.map(pin => (
              <HistoryCard key={pin.id} pin={pin} onReactivated={handleReactivated} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TripHistory;